import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays, MessageCircle, Plus, Trash2, X, ArrowLeft,
  Clock, MapPin, CheckCircle, Edit2, Save, Lock, LogOut, Flame, Upload,
  Megaphone, Star, Eye, EyeOff, Send, CircleDot, Image
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import axios from "axios";
import { format, parseISO } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultEvent = {
  title: "", description: "", date: "", time: "",
  location: "Gainsborough Sports Centre, 5 Braziers Road IP3 0SP",
  category: "service", is_recurring: false,
};

const LoginScreen = ({ onLogin, error, loading }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(username, password);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-stone-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-sm mx-6"
      >
        <div className="bg-white rounded-[2rem] p-8 shadow-[0_20px_60px_rgb(0,0,0,0.08)] border border-stone-100">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-stone-900">Admin Login</h2>
            <p className="text-sm text-stone-500 mt-1">Winners Chapel Ipswich</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Username</label>
              <Input
                data-testid="admin-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="rounded-xl border-stone-200 bg-stone-50 focus:ring-2 focus:ring-red-200 focus:border-red-400 px-4 py-3 h-auto"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Password</label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="rounded-xl border-stone-200 bg-stone-50 focus:ring-2 focus:ring-red-200 focus:border-red-400 px-4 py-3 h-auto"
                required
              />
            </div>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full bg-red-600 text-white hover:bg-red-700 rounded-full px-6 py-3.5 font-semibold shadow-md hover:shadow-lg transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export const AdminPanel = ({ onClose }) => {
  const [token, setToken] = useState(null);
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [propheticFocus, setPropheticFocus] = useState(null);
  const [focusForm, setFocusForm] = useState({ title: "", month: "", image_url: "", description: "" });
  const [savingFocus, setSavingFocus] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [newAnnUrl, setNewAnnUrl] = useState("");
  const [newAnnTitle, setNewAnnTitle] = useState("");
  const [annUploading, setAnnUploading] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const chatEndRef = useRef(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [newGalleryUrl, setNewGalleryUrl] = useState("");
  const [newGalleryCaption, setNewGalleryCaption] = useState("");
  const [editingGallery, setEditingGallery] = useState(null);
  const [editGalleryCaption, setEditGalleryCaption] = useState("");
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(defaultEvent);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const handleLogin = async (username, password) => {
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      setToken(res.data.token);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [evRes, prRes, pfRes, annRes, testRes, chatRes, galRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/prayer-requests`, { headers: authHeaders }),
        axios.get(`${API}/prophetic-focus`),
        axios.get(`${API}/announcements/all`, { headers: authHeaders }),
        axios.get(`${API}/testimonies/all`, { headers: authHeaders }),
        axios.get(`${API}/chat/sessions`, { headers: authHeaders }),
        axios.get(`${API}/gallery/all`, { headers: authHeaders }),
      ]);
      setEvents(evRes.data);
      setPrayers(prRes.data);
      setAnnouncements(annRes.data);
      setTestimonies(testRes.data);
      setChatSessions(chatRes.data);
      setGalleryItems(galRes.data);
      if (pfRes.data) {
        setPropheticFocus(pfRes.data);
        setFocusForm({
          title: pfRes.data.title || "",
          month: pfRes.data.month || "",
          image_url: pfRes.data.image_url || "",
          description: pfRes.data.description || "",
        });
      }
    } catch (err) {
      console.error("Admin fetch error:", err);
      if (err.response?.status === 401) {
        setToken(null);
        setLoginError("Session expired. Please login again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title || !eventForm.date || !eventForm.time) return;
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, eventForm, { headers: authHeaders });
      } else {
        await axios.post(`${API}/events`, eventForm, { headers: authHeaders });
      }
      setShowEventDialog(false);
      setEditingEvent(null);
      setEventForm(defaultEvent);
      fetchData();
    } catch (err) {
      console.error("Save event error:", err);
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Delete event error:", err);
    }
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title, description: event.description || "", date: event.date,
      time: event.time, location: event.location, category: event.category,
      is_recurring: event.is_recurring,
    });
    setShowEventDialog(true);
  };

  const openNewEvent = () => {
    setEditingEvent(null);
    setEventForm(defaultEvent);
    setShowEventDialog(true);
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.patch(`${API}/prayer-requests/${id}/read`, {}, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Mark read error:", err);
    }
  };

  const handleDeletePrayer = async (id) => {
    try {
      await axios.delete(`${API}/prayer-requests/${id}`, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Delete prayer error:", err);
    }
  };

  const handleSaveFocus = async () => {
    setSavingFocus(true);
    try {
      await axios.put(`${API}/prophetic-focus`, focusForm, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Save prophetic focus error:", err);
    } finally {
      setSavingFocus(false);
    }
  };

  const handleFocusImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: authHeaders,
      });
      const imageUrl = `${BACKEND_URL}${res.data.url}`;
      setFocusForm({ ...focusForm, image_url: imageUrl });
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  // Announcement handlers
  const handleAnnUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnnUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: authHeaders,
      });
      setNewAnnUrl(`${BACKEND_URL}${res.data.url}`);
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setAnnUploading(false);
    }
  };

  const handleAddAnnouncement = async () => {
    if (!newAnnUrl) return;
    try {
      await axios.post(`${API}/announcements`, { title: newAnnTitle, image_url: newAnnUrl }, { headers: authHeaders });
      setNewAnnUrl("");
      setNewAnnTitle("");
      fetchData();
    } catch (err) {
      console.error("Add announcement error:", err);
    }
  };

  const handleDeleteAnn = async (id) => {
    try {
      await axios.delete(`${API}/announcements/${id}`, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Delete announcement error:", err);
    }
  };

  const handleToggleAnn = async (id) => {
    try {
      await axios.patch(`${API}/announcements/${id}/toggle`, {}, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Toggle announcement error:", err);
    }
  };

  // Testimony handlers
  const handleApproveTestimony = async (id) => {
    try {
      await axios.patch(`${API}/testimonies/${id}/approve`, {}, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Approve testimony error:", err);
    }
  };

  const handleDeleteTestimony = async (id) => {
    try {
      await axios.delete(`${API}/testimonies/${id}`, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Delete testimony error:", err);
    }
  };

  // Gallery handlers
  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGalleryUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: authHeaders,
      });
      setNewGalleryUrl(`${BACKEND_URL}${res.data.url}`);
    } catch (err) {
      console.error("Gallery upload error:", err);
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleAddGalleryItem = async () => {
    if (!newGalleryUrl) return;
    try {
      await axios.post(`${API}/gallery`, { image_url: newGalleryUrl, caption: newGalleryCaption, order: galleryItems.length }, { headers: authHeaders });
      setNewGalleryUrl("");
      setNewGalleryCaption("");
      fetchData();
    } catch (err) {
      console.error("Add gallery item error:", err);
    }
  };

  const handleDeleteGalleryItem = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: authHeaders });
      fetchData();
    } catch (err) {
      console.error("Delete gallery item error:", err);
    }
  };

  const handleUpdateGalleryCaption = async (id) => {
    try {
      await axios.put(`${API}/gallery/${id}`, { caption: editGalleryCaption }, { headers: authHeaders });
      setEditingGallery(null);
      setEditGalleryCaption("");
      fetchData();
    } catch (err) {
      console.error("Update gallery caption error:", err);
    }
  };

  // Chat handlers
  const openChat = async (session) => {
    setActiveChat(session);
    try {
      const res = await axios.get(`${API}/chat/sessions/${session.id}/messages`);
      setChatMessages(res.data);
      await axios.post(`${API}/chat/sessions/${session.id}/read`, {}, { headers: authHeaders });
    } catch (err) {
      console.error("Open chat error:", err);
    }
  };

  const sendAdminMessage = async () => {
    if (!chatInput.trim() || !activeChat) return;
    try {
      await axios.post(`${API}/chat/sessions/${activeChat.id}/messages`, { message: chatInput.trim(), sender: "admin" });
      setChatInput("");
      const res = await axios.get(`${API}/chat/sessions/${activeChat.id}/messages`);
      setChatMessages(res.data);
    } catch (err) {
      console.error("Send admin message error:", err);
    }
  };

  const handleDeleteChat = async (sessionId) => {
    try {
      await axios.delete(`${API}/chat/sessions/${sessionId}`, { headers: authHeaders });
      if (activeChat?.id === sessionId) {
        setActiveChat(null);
        setChatMessages([]);
      }
      fetchData();
    } catch (err) {
      console.error("Delete chat error:", err);
    }
  };

  // Admin heartbeat when on chat tab
  useEffect(() => {
    if (tab !== "chat" || !token) return;
    const heartbeat = () => axios.post(`${API}/chat/admin-status`, {}, { headers: authHeaders }).catch(() => {});
    heartbeat();
    const interval = setInterval(heartbeat, 15000);
    return () => clearInterval(interval);
  }, [tab, token]);

  // Poll chat messages when viewing a chat
  useEffect(() => {
    if (!activeChat || tab !== "chat") return;
    const poll = () => axios.get(`${API}/chat/sessions/${activeChat.id}/messages`).then(r => setChatMessages(r.data)).catch(() => {});
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [activeChat, tab]);

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const categoryColors = {
    service: "bg-red-50 text-red-700",
    youth: "bg-blue-50 text-blue-700",
    outreach: "bg-green-50 text-green-700",
    prayer: "bg-amber-50 text-amber-700",
    special: "bg-purple-50 text-purple-700",
  };

  // Show login if not authenticated
  if (!token) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-stone-50"
        data-testid="admin-panel"
      >
        <button
          onClick={onClose}
          data-testid="admin-close-btn"
          className="absolute top-6 left-6 z-10 p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-all text-stone-600 hover:text-stone-900"
        >
          <ArrowLeft size={20} />
        </button>
        <LoginScreen onLogin={handleLogin} error={loginError} loading={loginLoading} />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-stone-50 overflow-auto"
      data-testid="admin-panel"
    >
      {/* Admin header */}
      <div className="bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            data-testid="admin-back-btn"
            className="p-2 rounded-full hover:bg-stone-100 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-bold text-stone-900">Admin Panel</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setTab("events")}
              data-testid="admin-tab-events"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "events" ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              <CalendarDays size={14} className="inline mr-1.5" />
              Events ({events.length})
            </button>
            <button
              onClick={() => setTab("prayers")}
              data-testid="admin-tab-prayers"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "prayers" ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              <MessageCircle size={14} className="inline mr-1.5" />
              Prayers ({prayers.filter(p => !p.is_read).length} new)
            </button>
            <button
              onClick={() => setTab("focus")}
              data-testid="admin-tab-focus"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "focus" ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              <Flame size={14} className="inline mr-1.5" />
              Focus
            </button>
            <button
              onClick={() => setTab("announcements")}
              data-testid="admin-tab-announcements"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "announcements" ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              <Megaphone size={14} className="inline mr-1.5" />
              Announcements
            </button>
            <button
              onClick={() => setTab("testimonies")}
              data-testid="admin-tab-testimonies"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "testimonies" ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              <Star size={14} className="inline mr-1.5" />
              Testimonies ({testimonies.filter(t => !t.is_approved).length})
            </button>
            <button
              onClick={() => setTab("chat")}
              data-testid="admin-tab-chat"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "chat" ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              <MessageCircle size={14} className="inline mr-1.5" />
              Chat ({chatSessions.reduce((acc, s) => acc + (s.unread || 0), 0)})
            </button>
            <button
              onClick={() => setTab("gallery")}
              data-testid="admin-tab-gallery"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "gallery" ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              <Image size={14} className="inline mr-1.5" />
              Gallery ({galleryItems.length})
            </button>
          </div>
          <button
            onClick={() => { setToken(null); onClose(); }}
            data-testid="admin-logout-btn"
            className="p-2 rounded-full hover:bg-stone-100 text-stone-400 hover:text-red-600 transition-colors"
            title="Logout"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-white rounded-2xl animate-pulse border border-stone-100" />
            ))}
          </div>
        ) : tab === "events" ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-stone-900">Manage Events</h2>
              <button
                onClick={openNewEvent}
                data-testid="admin-add-event-btn"
                className="flex items-center gap-2 bg-red-600 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-red-700 transition-colors"
              >
                <Plus size={16} />
                Add Event
              </button>
            </div>

            <div className="space-y-3">
              {events.length === 0 ? (
                <div className="text-center py-16 text-stone-400">
                  <CalendarDays size={48} className="mx-auto mb-3 opacity-40" />
                  <p>No events yet. Create your first event!</p>
                </div>
              ) : (
                events.map((event) => (
                  <div
                    key={event.id}
                    className="bg-white p-5 rounded-2xl border border-stone-100 shadow-[0_2px_10px_rgb(0,0,0,0.03)] flex items-center gap-4"
                    data-testid={`admin-event-${event.id}`}
                  >
                    <div className="shrink-0 w-14 h-14 rounded-xl bg-red-50 flex flex-col items-center justify-center">
                      <span className="text-[10px] font-bold text-red-600 uppercase">
                        {(() => { try { return format(parseISO(event.date), "MMM"); } catch { return ""; } })()}
                      </span>
                      <span className="text-lg font-bold text-red-700">
                        {(() => { try { return format(parseISO(event.date), "d"); } catch { return ""; } })()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-stone-900 text-sm">{event.title}</h3>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${categoryColors[event.category] || categoryColors.service}`}>
                          {event.category}
                        </span>
                      </div>
                      <div className="flex gap-3 mt-1 text-xs text-stone-400">
                        <span className="flex items-center gap-1"><Clock size={10} /> {event.time}</span>
                        <span className="flex items-center gap-1"><MapPin size={10} /> {event.location}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => openEditEvent(event)}
                        data-testid={`admin-edit-event-${event.id}`}
                        className="p-2 rounded-xl hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(event.id)}
                        data-testid={`admin-delete-event-${event.id}`}
                        className="p-2 rounded-xl hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : tab === "prayers" ? (
          <>
            <h2 className="text-lg font-bold text-stone-900 mb-6">Prayer Requests</h2>
            <div className="space-y-3">
              {prayers.length === 0 ? (
                <div className="text-center py-16 text-stone-400">
                  <MessageCircle size={48} className="mx-auto mb-3 opacity-40" />
                  <p>No prayer requests yet.</p>
                </div>
              ) : (
                prayers.map((prayer) => (
                  <div
                    key={prayer.id}
                    className={`bg-white p-5 rounded-2xl border shadow-[0_2px_10px_rgb(0,0,0,0.03)] ${
                      prayer.is_read ? "border-stone-100 opacity-70" : "border-red-100"
                    }`}
                    data-testid={`admin-prayer-${prayer.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-stone-900 text-sm">{prayer.name}</h3>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                            {prayer.request_type}
                          </span>
                          {!prayer.is_read && (
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                          )}
                        </div>
                        {prayer.email && <p className="text-xs text-stone-400">{prayer.email}</p>}
                        <p className="text-sm text-stone-600 mt-2">{prayer.message}</p>
                        <p className="text-[10px] text-stone-300 mt-2">
                          {(() => { try { return format(parseISO(prayer.created_at), "PPP p"); } catch { return prayer.created_at; } })()}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {!prayer.is_read && (
                          <button
                            onClick={() => handleMarkRead(prayer.id)}
                            data-testid={`admin-mark-read-${prayer.id}`}
                            className="p-2 rounded-xl hover:bg-green-50 text-stone-400 hover:text-green-600 transition-colors"
                            title="Mark as read"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePrayer(prayer.id)}
                          data-testid={`admin-delete-prayer-${prayer.id}`}
                          className="p-2 rounded-xl hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : tab === "focus" ? (
          <>
            <h2 className="text-lg font-bold text-stone-900 mb-6">Prophetic Focus of the Month</h2>
            <div className="bg-white p-6 rounded-2xl border border-stone-100 shadow-[0_2px_10px_rgb(0,0,0,0.03)] space-y-5" data-testid="admin-focus-form">
              {/* Current preview */}
              {focusForm.image_url && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-stone-500 mb-2">Current Image Preview</p>
                  <img
                    src={focusForm.image_url}
                    alt="Prophetic focus preview"
                    className="w-full max-w-md h-auto border border-stone-200 rounded-xl"
                    data-testid="admin-focus-preview"
                  />
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1 block">Title</label>
                  <Input
                    data-testid="admin-focus-title"
                    value={focusForm.title}
                    onChange={(e) => setFocusForm({ ...focusForm, title: e.target.value })}
                    placeholder="e.g., Prophetic Focus for the Month"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1 block">Month</label>
                  <Input
                    data-testid="admin-focus-month"
                    value={focusForm.month}
                    onChange={(e) => setFocusForm({ ...focusForm, month: e.target.value })}
                    placeholder="e.g., March 2026"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 mb-2 block">Image</label>
                <div className="space-y-3">
                  <label
                    data-testid="admin-focus-upload-btn"
                    className={`flex items-center justify-center gap-2 w-full border-2 border-dashed rounded-xl px-4 py-4 cursor-pointer transition-colors ${
                      uploading ? "border-stone-300 bg-stone-50 text-stone-400" : "border-red-200 bg-red-50/50 text-red-600 hover:bg-red-50 hover:border-red-300"
                    }`}
                  >
                    <Upload size={18} />
                    <span className="text-sm font-medium">
                      {uploading ? "Uploading..." : "Upload New Image"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFocusImageUpload}
                      className="hidden"
                      disabled={uploading}
                      data-testid="admin-focus-file-input"
                    />
                  </label>
                  <Input
                    data-testid="admin-focus-image-url"
                    value={focusForm.image_url}
                    onChange={(e) => setFocusForm({ ...focusForm, image_url: e.target.value })}
                    placeholder="Or paste image URL here"
                    className="rounded-xl text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Description</label>
                <Textarea
                  data-testid="admin-focus-description"
                  value={focusForm.description}
                  onChange={(e) => setFocusForm({ ...focusForm, description: e.target.value })}
                  placeholder="e.g., My Month of Supernatural Satisfactions"
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <button
                onClick={handleSaveFocus}
                disabled={savingFocus}
                data-testid="admin-focus-save-btn"
                className="flex items-center gap-2 bg-red-600 text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                <Save size={14} />
                {savingFocus ? "Saving..." : "Update Prophetic Focus"}
              </button>
            </div>
          </>
        ) : tab === "announcements" ? (
          <>
            <h2 className="text-lg font-bold text-stone-900 mb-6">Manage Announcements</h2>

            {/* Add new announcement */}
            <div className="bg-white p-5 rounded-2xl border border-stone-100 shadow-[0_2px_10px_rgb(0,0,0,0.03)] mb-6 space-y-4" data-testid="admin-add-announcement">
              <p className="text-sm font-semibold text-stone-700">Add New Announcement</p>
              <Input
                data-testid="admin-ann-title-input"
                value={newAnnTitle}
                onChange={(e) => setNewAnnTitle(e.target.value)}
                placeholder="Announcement title (optional)"
                className="rounded-xl"
              />
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label
                    className={`flex items-center justify-center gap-2 w-full border-2 border-dashed rounded-xl px-4 py-3 cursor-pointer transition-colors ${
                      annUploading ? "border-stone-300 bg-stone-50 text-stone-400" : "border-red-200 bg-red-50/50 text-red-600 hover:bg-red-50"
                    }`}
                  >
                    <Upload size={16} />
                    <span className="text-sm font-medium">{annUploading ? "Uploading..." : newAnnUrl ? "Image uploaded" : "Upload Image"}</span>
                    <input type="file" accept="image/*" onChange={handleAnnUpload} className="hidden" disabled={annUploading} data-testid="admin-ann-file-input" />
                  </label>
                </div>
                <button
                  onClick={handleAddAnnouncement}
                  disabled={!newAnnUrl}
                  data-testid="admin-ann-add-btn"
                  className="flex items-center gap-2 bg-red-600 text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-40"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
              {newAnnUrl && (
                <img src={newAnnUrl} alt="Preview" className="w-full max-w-xs h-auto rounded-xl border border-stone-200" />
              )}
            </div>

            {/* Announcement list */}
            <div className="space-y-3">
              {announcements.length === 0 ? (
                <div className="text-center py-12 text-stone-400">
                  <Megaphone size={40} className="mx-auto mb-3 opacity-40" />
                  <p>No announcements yet.</p>
                </div>
              ) : (
                announcements.map((ann) => (
                  <div
                    key={ann.id}
                    className={`bg-white p-4 rounded-2xl border shadow-[0_2px_10px_rgb(0,0,0,0.03)] flex items-center gap-4 ${ann.is_active ? "border-stone-100" : "border-stone-200 opacity-50"}`}
                    data-testid={`admin-ann-${ann.id}`}
                  >
                    <img src={ann.image_url} alt={ann.title} className="w-24 h-14 object-cover rounded-xl shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-stone-900 truncate">{ann.title || "Untitled"}</p>
                      <p className="text-[10px] text-stone-400">{ann.is_active ? "Active" : "Hidden"}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => handleToggleAnn(ann.id)} data-testid={`admin-toggle-ann-${ann.id}`}
                        className="p-2 rounded-xl hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                        title={ann.is_active ? "Hide" : "Show"}>
                        {ann.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button onClick={() => handleDeleteAnn(ann.id)} data-testid={`admin-delete-ann-${ann.id}`}
                        className="p-2 rounded-xl hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : tab === "testimonies" ? (
          <>
            <h2 className="text-lg font-bold text-stone-900 mb-6">Manage Testimonies</h2>
            <div className="space-y-3">
              {testimonies.length === 0 ? (
                <div className="text-center py-12 text-stone-400">
                  <Star size={40} className="mx-auto mb-3 opacity-40" />
                  <p>No testimonies submitted yet.</p>
                </div>
              ) : (
                testimonies.map((t) => (
                  <div
                    key={t.id}
                    className={`bg-white p-5 rounded-2xl border shadow-[0_2px_10px_rgb(0,0,0,0.03)] ${t.is_approved ? "border-green-100" : "border-amber-200"}`}
                    data-testid={`admin-testimony-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm text-stone-900">{t.name}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.is_approved ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                            {t.is_approved ? "Approved" : "Pending"}
                          </span>
                        </div>
                        <p className="text-sm text-stone-600">"{t.message}"</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {!t.is_approved && (
                          <button onClick={() => handleApproveTestimony(t.id)} data-testid={`admin-approve-testimony-${t.id}`}
                            className="p-2 rounded-xl hover:bg-green-50 text-stone-400 hover:text-green-600 transition-colors" title="Approve">
                            <CheckCircle size={16} />
                          </button>
                        )}
                        <button onClick={() => handleDeleteTestimony(t.id)} data-testid={`admin-delete-testimony-${t.id}`}
                          className="p-2 rounded-xl hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : tab === "chat" ? (
          <div className="flex gap-4 h-[calc(100vh-180px)]" data-testid="admin-chat-section">
            {/* Sessions list */}
            <div className="w-72 shrink-0 bg-white rounded-2xl border border-stone-100 shadow-[0_2px_10px_rgb(0,0,0,0.03)] overflow-y-auto">
              <div className="p-4 border-b border-stone-100">
                <div className="flex items-center gap-2">
                  <CircleDot size={12} className="text-green-500" />
                  <span className="text-xs font-semibold text-green-700">You are online</span>
                </div>
              </div>
              {chatSessions.length === 0 ? (
                <div className="text-center py-12 text-stone-400 px-4">
                  <MessageCircle size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No conversations yet</p>
                  <p className="text-xs mt-1">When visitors start a chat, they'll appear here.</p>
                </div>
              ) : (
                chatSessions.map((s) => (
                  <div key={s.id} className="flex items-center border-b border-stone-50">
                    <button
                      onClick={() => openChat(s)}
                      data-testid={`admin-chat-session-${s.id}`}
                      className={`flex-1 text-left p-4 hover:bg-red-50 transition-colors ${activeChat?.id === s.id ? "bg-red-50" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-stone-900">{s.visitor_name}</span>
                        {s.unread > 0 && (
                          <span className="w-5 h-5 bg-red-600 text-white rounded-full text-[10px] font-bold flex items-center justify-center">{s.unread}</span>
                        )}
                      </div>
                      <p className="text-xs text-stone-400 mt-0.5 truncate">{s.last_message || "New conversation"}</p>
                    </button>
                    <button
                      onClick={() => handleDeleteChat(s.id)}
                      data-testid={`admin-delete-chat-${s.id}`}
                      className="p-2 mr-2 rounded-xl hover:bg-red-50 text-stone-300 hover:text-red-600 transition-colors"
                      title="Delete conversation"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Chat area */}
            <div className="flex-1 bg-white rounded-2xl border border-stone-100 shadow-[0_2px_10px_rgb(0,0,0,0.03)] flex flex-col overflow-hidden">
              {!activeChat ? (
                <div className="flex-1 flex items-center justify-center text-stone-400">
                  <div className="text-center">
                    <MessageCircle size={40} className="mx-auto mb-3 opacity-40" />
                    <p className="text-sm">Select a conversation</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-xs">
                      {activeChat.visitor_name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-semibold text-stone-900">{activeChat.visitor_name}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-chat-messages">
                    {chatMessages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                          msg.sender === "admin"
                            ? "bg-red-600 text-white rounded-br-md"
                            : "bg-stone-100 text-stone-800 rounded-bl-md"
                        }`}>
                          {msg.message}
                        </div>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                  <div className="border-t border-stone-200 px-4 py-3 flex gap-2">
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && sendAdminMessage()}
                      placeholder="Type a reply..."
                      className="flex-1 px-4 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                      data-testid="admin-chat-input"
                    />
                    <button
                      onClick={sendAdminMessage}
                      disabled={!chatInput.trim()}
                      data-testid="admin-chat-send-btn"
                      className="w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center justify-center transition-colors disabled:opacity-40"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : tab === "gallery" ? (
          <>
            <h2 className="text-lg font-bold text-stone-900 mb-6">Manage Gallery</h2>

            {/* Add new gallery item */}
            <div className="bg-white p-5 rounded-2xl border border-stone-100 shadow-[0_2px_10px_rgb(0,0,0,0.03)] mb-6 space-y-4" data-testid="admin-add-gallery">
              <p className="text-sm font-semibold text-stone-700">Add New Photo</p>
              <Input
                data-testid="admin-gallery-caption-input"
                value={newGalleryCaption}
                onChange={(e) => setNewGalleryCaption(e.target.value)}
                placeholder="Photo caption (optional)"
                className="rounded-xl"
              />
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label
                    className={`flex items-center justify-center gap-2 w-full border-2 border-dashed rounded-xl px-4 py-3 cursor-pointer transition-colors ${
                      galleryUploading ? "border-stone-300 bg-stone-50 text-stone-400" : "border-red-200 bg-red-50/50 text-red-600 hover:bg-red-50"
                    }`}
                  >
                    <Upload size={16} />
                    <span className="text-sm font-medium">{galleryUploading ? "Uploading..." : newGalleryUrl ? "Image uploaded" : "Upload Photo"}</span>
                    <input type="file" accept="image/*" onChange={handleGalleryUpload} className="hidden" disabled={galleryUploading} data-testid="admin-gallery-file-input" />
                  </label>
                </div>
                <button
                  onClick={handleAddGalleryItem}
                  disabled={!newGalleryUrl}
                  data-testid="admin-gallery-add-btn"
                  className="flex items-center gap-2 bg-red-600 text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-40"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
              {newGalleryUrl && (
                <img src={newGalleryUrl} alt="Preview" className="w-full max-w-xs h-auto rounded-xl border border-stone-200" data-testid="admin-gallery-preview" />
              )}
            </div>

            {/* Gallery items list */}
            <div className="space-y-3">
              {galleryItems.length === 0 ? (
                <div className="text-center py-12 text-stone-400">
                  <Image size={40} className="mx-auto mb-3 opacity-40" />
                  <p>No gallery photos yet.</p>
                </div>
              ) : (
                galleryItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-2xl border border-stone-100 shadow-[0_2px_10px_rgb(0,0,0,0.03)] flex items-center gap-4"
                    data-testid={`admin-gallery-item-${item.id}`}
                  >
                    <img src={item.image_url} alt={item.caption} className="w-24 h-16 object-cover rounded-xl shrink-0" />
                    <div className="flex-1 min-w-0">
                      {editingGallery === item.id ? (
                        <div className="flex gap-2">
                          <Input
                            value={editGalleryCaption}
                            onChange={(e) => setEditGalleryCaption(e.target.value)}
                            className="rounded-xl text-sm"
                            data-testid={`admin-gallery-edit-caption-${item.id}`}
                          />
                          <button
                            onClick={() => handleUpdateGalleryCaption(item.id)}
                            className="p-2 rounded-xl hover:bg-green-50 text-green-600 transition-colors"
                            data-testid={`admin-gallery-save-caption-${item.id}`}
                          >
                            <Save size={16} />
                          </button>
                          <button
                            onClick={() => { setEditingGallery(null); setEditGalleryCaption(""); }}
                            className="p-2 rounded-xl hover:bg-stone-100 text-stone-400 transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-stone-700 truncate">{item.caption || "No caption"}</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {editingGallery !== item.id && (
                        <button
                          onClick={() => { setEditingGallery(item.id); setEditGalleryCaption(item.caption || ""); }}
                          data-testid={`admin-gallery-edit-${item.id}`}
                          className="p-2 rounded-xl hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteGalleryItem(item.id)}
                        data-testid={`admin-gallery-delete-${item.id}`}
                        className="p-2 rounded-xl hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : null}
      </div>
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="rounded-[2rem] max-w-lg z-[200]" data-testid="event-dialog" style={{ zIndex: 200 }}>
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Edit Event" : "New Event"}</DialogTitle>
            <DialogDescription>
              {editingEvent ? "Update event details" : "Fill in the event details"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Title *</label>
              <Input
                data-testid="event-title-input"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Event title"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Description</label>
              <Textarea
                data-testid="event-description-input"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Event description"
                className="rounded-xl resize-none"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Date *</label>
                <Input
                  data-testid="event-date-input"
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Time *</label>
                <Input
                  data-testid="event-time-input"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                  placeholder="e.g., 10:00 AM"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Location</label>
              <Input
                data-testid="event-location-input"
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Category</label>
                <Select
                  value={eventForm.category}
                  onValueChange={(v) => setEventForm({ ...eventForm, category: v })}
                >
                  <SelectTrigger data-testid="event-category-select" className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="youth">Youth</SelectItem>
                    <SelectItem value="outreach">Outreach</SelectItem>
                    <SelectItem value="prayer">Prayer</SelectItem>
                    <SelectItem value="special">Special</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={eventForm.is_recurring}
                    onChange={(e) => setEventForm({ ...eventForm, is_recurring: e.target.checked })}
                    className="rounded"
                    data-testid="event-recurring-checkbox"
                  />
                  <span className="text-sm font-medium text-stone-700">Recurring event</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowEventDialog(false)}
              className="px-5 py-2.5 rounded-full text-sm text-stone-600 hover:bg-stone-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEvent}
              data-testid="event-save-btn"
              className="flex items-center gap-2 bg-red-600 text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-red-700 transition-colors"
            >
              <Save size={14} />
              {editingEvent ? "Update" : "Create"} Event
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
